'use client';
import { useState, useEffect } from 'react';
import Button from '@/ui/Button';
import Input from '@/components/Input';
import { components, OptionProps } from 'react-select';
import { GearIcon, TimerIcon, CodeIcon, ArrowRightIcon } from '@radix-ui/react-icons';
import { IModalResource } from '@/lib/modalResourcesApi';
import { INotebookConfig, IBaseImage } from '@/lib/notebooks/notebooksApi';
import ConfigSlider from '@/components/Slider';
import { Stack } from '@/ui/primatives';
import * as Styled from './styled';
import { Simple as Tooltip } from '@/ui/Tooltip';
import Link from 'next/link';
import Flash from '@/ui/Flash';
import ScriptEditor from './ScriptEditor';
import BaseImageSelect from '../../BaseImageSelect';

type GpuOptionProps = {
  data: { label: string; costPerHour: number };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} & OptionProps<any>;

export const GpuOption = ({ data, ...otherProps }: GpuOptionProps) => {
  return (
    <components.Option data={data} {...otherProps}>
      <Styled.GpuOptionValue>
        <div className="item">
          <span className="label">{data.label}</span>
          {data.costPerHour && <span className="cost-per-hour">${data.costPerHour.toFixed(2)}/hr</span>}
        </div>
      </Styled.GpuOptionValue>
    </components.Option>
  );
};

const ImageConfiguration: React.FC<{
  baseImageList: IBaseImage[];
  config?: INotebookConfig;
  open: boolean;
  onBaseImageChange: (v: string) => void;
  onBuildScriptChange: (v: string) => void;
}> = ({ baseImageList, config, open, onBaseImageChange, onBuildScriptChange }) => {
  // Find the default base image
  const defaultBaseImage = baseImageList.find((image) => image.is_default);

  // if config?.notebook_base_image.id does not exist in baseImageList, set it to the defaultBaseImage
  // the reason this might happen is if we have depreciated a base image and the user has not updated their config
  var baseId = config?.notebook_base_image.id;
  if (!baseImageList.find((image) => image.id === config?.notebook_base_image.id)) {
    baseId = defaultBaseImage?.id;
  }

  const [buildScript, setBuildScript] = useState(config?.notebook_image.build_script || '');
  const [baseImageId, setBaseImageId] = useState(baseId || '');

  useEffect(() => {
    onBaseImageChange(baseImageId);
  }, [baseImageId]);

  useEffect(() => {
    onBuildScriptChange(buildScript);
  }, [buildScript]);

  return (
    <Styled.ImageTemplateWrapper $open={open}>
      <Styled.ImageTemplate>
        <h2>Image configuration</h2>
        <BaseImageSelect baseImageList={baseImageList} selectedId={baseImageId} onChange={(v) => setBaseImageId(v)} />
        {
          <Styled.ScriptEditorWrapper>
            <label>Build Script</label>
            <Styled.ScriptEditor>
              <ScriptEditor
                language="bash"
                content={buildScript}
                onChange={(v) => {
                  setBuildScript(v);
                }}
              />
            </Styled.ScriptEditor>
          </Styled.ScriptEditorWrapper>
        }
      </Styled.ImageTemplate>
    </Styled.ImageTemplateWrapper>
  );
};

type MarimoSandboxMenuProps = {
  modalResources: IModalResource[];
  isLoading: boolean;
  onSubmit: (values: {
    gpuModel: string;
    cpuCores: number;
    memory: number;
    runtime: number;
    notebookImageId?: string;
    notebookBaseImageId: string;
    buildScript?: string;
  }) => void;
  hasAccess: boolean;
  config?: INotebookConfig;
  baseImageList: IBaseImage[];
  isLoggedinUser?: boolean;
  isNotEnoughCredits: boolean;
};

const MarimoSandboxMenu: React.FC<MarimoSandboxMenuProps> = ({
  modalResources,
  isLoading,
  onSubmit,
  // initialBuildScript,
  hasAccess,
  config,
  baseImageList,
  isLoggedinUser,
  isNotEnoughCredits,
}) => {
  const [gpuModel, setGpuModel] = useState('');
  const cpuCoreOptions = [1, 2, 4, 8, 16, 32, 64];
  const memoryOptions = [1, 2, 4, 8, 16, 32, 64];
  const [cpuCoreIndex, setCpuCoreIndex] = useState(1);
  const [memoryIndex, setMemoryIndex] = useState(1);
  const [runtime, setRuntime] = useState(1);
  const [isImageTemplateOpen, setIsImageTemplateOpen] = useState(false);
  const [buildScript, setBuildScript] = useState(config?.notebook_image.build_script || '');
  const [baseImageId, setBaseImageId] = useState(config?.notebook_base_image.id || 'config-not-found');
  const gpuResources = modalResources.filter((resource) => resource.type === 'gpu');
  const cpuResources = modalResources.filter((resource) => resource.type === 'cpu');
  const memoryResources = modalResources.filter((resource) => resource.type === 'memory');
  if (
    modalResources.length === 0 ||
    gpuResources.length === 0 ||
    cpuResources.length === 0 ||
    memoryResources.length === 0
  ) {
    return null;
  }

  useEffect(() => {
    if (config) {
      setGpuModel(config.gpu_model || '');

      const foundCpuIndex = cpuCoreOptions.findIndex((option) => option === config.cpu_cores);
      setCpuCoreIndex(foundCpuIndex !== -1 ? foundCpuIndex : 1);

      const memoryGB = config.memory_mb ? Math.round(config.memory_mb / 1024) : undefined;

      const foundMemoryIndex = memoryGB !== undefined ? memoryOptions.findIndex((option) => option === memoryGB) : -1;

      setMemoryIndex(foundMemoryIndex !== -1 ? foundMemoryIndex : 1);
    }
  }, []);

  const cpuPricePerSecond = cpuResources.length > 0 ? cpuResources[0].unit_price_per_second : 0;
  const memoryPricePerSecond = memoryResources.length > 0 ? memoryResources[0].unit_price_per_second : 0;
  const isBuildScriptDirty = buildScript !== config?.notebook_image.build_script;

  const gpuOptions = [
    { value: '', label: 'None' },
    ...gpuResources.map((resource) => ({
      value: resource.resource_slug,
      label: resource.display_title,
      costPerHour: resource.unit_price_per_second * 3600,
    })),
  ];

  const cpuCores = cpuCoreOptions[cpuCoreIndex];
  const memory = memoryOptions[memoryIndex];

  const handleMemoryChange = (index: number) => {
    setMemoryIndex(index);
  };

  const handleCpuCoresChange = (index: number) => {
    setCpuCoreIndex(index);
  };

  const calculatePrice = () => {
    const secondsToHours = 3600;

    const cpuTotalPrice = cpuCores * cpuPricePerSecond * secondsToHours;
    const memoryTotalPrice = memory * memoryPricePerSecond * secondsToHours;

    const selectedGpu = gpuResources.find((resource) => resource.resource_slug === gpuModel);
    const gpuTotalPrice = selectedGpu ? selectedGpu.unit_price_per_second * secondsToHours : 0;

    const hourlyPrice = cpuTotalPrice + memoryTotalPrice + gpuTotalPrice;

    return hourlyPrice.toFixed(2);
  };

  return (
    <Styled.Menu $open={isImageTemplateOpen}>
      <div>
        <Styled.Configuration>
          <Styled.SliderContainer>
            <h2>Notebook configuration</h2>

            {
              <Styled.EditScriptLink onClick={() => setIsImageTemplateOpen(!isImageTemplateOpen)}>
                <CodeIcon />
                Customize build script
                <ArrowRightIcon />
              </Styled.EditScriptLink>
            }

            <Stack gap={10}>
              <Styled.SectionHeading>
                <div>
                  <GearIcon />
                  GPU Model
                </div>
              </Styled.SectionHeading>

              <Input.SelectControl
                name="gpu-model"
                wide
                disabled={isLoading}
                options={gpuOptions}
                selectedValue={gpuModel}
                onChange={(value) => setGpuModel(value as string)}
                isSearchable={false}
                placeholder="Select GPU model"
                customComponents={{ Option: GpuOption }}
                styles={{
                  option: (styles) => ({ ...styles, whiteSpace: 'nowrap' }),
                  control: (styles) => ({ ...styles }),
                }}
              />
            </Stack>

            <Stack gap={10}>
              <Styled.SectionHeading>
                <div>
                  <GearIcon />
                  CPU Cores
                </div>
                <div>{cpuCores.toString()} cores</div>
              </Styled.SectionHeading>
              <ConfigSlider
                value={cpuCoreIndex}
                onChange={handleCpuCoresChange}
                min={0}
                max={cpuCoreOptions.length - 1}
                step={1}
                label="CPU Cores"
              />
            </Stack>

            <Stack gap={10}>
              <Styled.SectionHeading>
                <div>
                  <GearIcon />
                  Memory
                </div>
                <div>{memory.toString()} GB</div>
              </Styled.SectionHeading>
              <ConfigSlider
                value={memoryIndex}
                onChange={handleMemoryChange}
                min={0}
                max={memoryOptions.length - 1}
                step={1}
                label="Memory"
              />
            </Stack>

            <Stack gap={10}>
              <Styled.SectionHeading>
                <div>
                  <TimerIcon />
                  Max Runtime
                </div>
                <div>{runtime.toString()}h</div>
              </Styled.SectionHeading>
              <ConfigSlider value={runtime} onChange={setRuntime} min={1} max={24} step={1} label="Runtime" />
            </Stack>
          </Styled.SliderContainer>
        </Styled.Configuration>

        <ImageConfiguration
          baseImageList={baseImageList}
          config={config}
          open={isImageTemplateOpen}
          onBaseImageChange={(v) => setBaseImageId(v)}
          onBuildScriptChange={(v) => setBuildScript(v)}
        />

        {/* <Styled.ImageTemplateWrapper $open={isImageTemplateOpen}>
          <Styled.ImageTemplate>
            <h2>Image build script</h2>
            {buildScript && (
              <Styled.ScriptEditor>
                <ScriptEditor
                  language="bash"
                  content={buildScript}
                  onChange={(v) => {
                    setBuildScript(v);
                  }}
                />
              </Styled.ScriptEditor>
            )}
          </Styled.ImageTemplate>
        </Styled.ImageTemplateWrapper> */}
      </div>
      <Styled.Footer>
        <div className="footer-content">
          <Styled.Pricing>
            <div>Estimated Price:</div>
            <div>
              ${calculatePrice()} <span>/hour</span>
            </div>
          </Styled.Pricing>
          <Tooltip
            content={
              isLoggedinUser
                ? "You don't have permission to start a notebook on this repository"
                : 'You must be logged in to start a notebook'
            }
            disabled={hasAccess}
          >
            <Button.Black
              small
              onClick={() =>
                onSubmit({
                  gpuModel,
                  cpuCores,
                  memory,
                  runtime,
                  notebookBaseImageId: baseImageId,
                  buildScript: isBuildScriptDirty ? buildScript : undefined,
                  notebookImageId: isBuildScriptDirty ? undefined : config?.notebook_image.id,
                })
              }
              disabled={isLoading || !hasAccess}
              stretch
            >
              {isLoading ? 'Starting...' : 'Start Notebook'}
            </Button.Black>
          </Tooltip>
          {isNotEnoughCredits && (
            <Flash type="danger" style={{ textAlign: 'center', padding: '8px' }}>
              <div>
                <div style={{ paddingBottom: '5px' }}>
                  <span style={{ fontWeight: 'bold' }}>Insufficient credits.</span>
                </div>
                <Link href="/billing/plans" style={{ textDecoration: 'underline', color: 'inherit' }}>
                  Add more credits,
                </Link>{' '}
                reduce settings or reduce runtime.
              </div>
            </Flash>
          )}
        </div>
      </Styled.Footer>
    </Styled.Menu>
  );
};

export default MarimoSandboxMenu;
